import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, playlist as playlistApi } from "../lib/api";
import DJConsole from "../components/DJConsole";
import PlaylistTab from "../components/PlaylistTab";
import SettingsTab from "../components/SettingsTab";
import FooterNav from "../components/FooterNav";
import PayGate from "../components/PayGate";
import { Disc3 } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const nav = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("dj");
  const [playlist, setPlaylist] = useState({ songs: [], share_token: "" });
  const [deckA, setDeckA] = useState(null);
  const [deckB, setDeckB] = useState(null);

  const refreshUser = useCallback(async () => {
    try {
      const u = await auth.me();
      setUser(u);
    } catch (e) {
      nav("/");
    }
  }, [nav]);

  const refreshPlaylist = useCallback(async () => {
    try {
      const pl = await playlistApi.get();
      setPlaylist(pl);
    } catch (_) {}
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const u = await auth.me();
        setUser(u);
        const pl = await playlistApi.get();
        setPlaylist(pl);
      } catch (e) {
        nav("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  // Handle payment callback (?order_id=...) - poll status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get("order_id");
    if (orderId && location.pathname.includes("/payment/callback")) {
      toast("Verifying payment...");
      import("../lib/api").then(({ payments }) => {
        payments
          .getOrder(orderId)
          .then((res) => {
            if (res?.order?.status === "paid" || res?.cashfree_status === "PAID") {
              toast.success("Payment successful! Plan activated.");
            } else {
              toast(`Payment status: ${res?.cashfree_status || res?.order?.status || "pending"}`);
            }
            refreshUser();
            nav("/app", { replace: true });
          })
          .catch(() => {
            toast.error("Could not verify payment");
            nav("/app", { replace: true });
          });
      });
    }
  }, [location, nav, refreshUser]);

  const onLogout = async () => {
    await auth.logout();
    nav("/");
  };

  const handleLoadDeck = (side, song) => {
    if (side === "A") setDeckA(song);
    else setDeckB(song);
    setTab("dj");
    toast.success(`Loaded to Deck ${side}: ${song.title}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full border-2 border-fuchsia-500 border-t-transparent animate-spin mb-4" />
          <div className="font-mono text-xs tracking-widest text-zinc-400">LOADING DECKS...</div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // If user has no active plan, gate everything behind PayGate
  if (!user.plan_active) {
    return <PayGate user={user} refreshUser={refreshUser} onLogout={onLogout} />;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-24 grain relative">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg,#d946ef,#06b6d4)",
                boxShadow: "0 0 20px rgba(217,70,239,0.4)",
              }}
            >
              <Disc3 className="w-4 h-4 text-black" />
            </div>
            <div>
              <div className="font-heading font-black text-sm leading-tight">
                {user.party_name || "House Party"}
              </div>
              <div className="font-mono text-[9px] tracking-widest text-zinc-500">
                DJ · {user.name}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PlanBadge user={user} />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* DJ Console stays mounted so YouTube iframes keep playing when the user
            switches to Playlist or Settings tabs. Other tabs mount on demand. */}
        <div style={{ display: tab === "dj" ? "block" : "none" }}>
          <DJConsole
            tracks={playlist.songs}
            deckA={deckA}
            setDeckA={setDeckA}
            deckB={deckB}
            setDeckB={setDeckB}
            onLoadDeck={handleLoadDeck}
            refreshPlaylist={refreshPlaylist}
          />
        </div>
        {tab === "playlist" && (
          <PlaylistTab
            playlist={playlist}
            refresh={refreshPlaylist}
            onLoadDeck={handleLoadDeck}
          />
        )}
        {tab === "settings" && (
          <SettingsTab user={user} refreshUser={refreshUser} onLogout={onLogout} />
        )}
      </main>

      <FooterNav tab={tab} setTab={setTab} />
    </div>
  );
}

function PlanBadge({ user }) {
  if (!user?.plan_expires_at) return null;
  const isSingle = user.plan === "single_party_99";
  const exp = new Date(user.plan_expires_at);
  const now = new Date();
  const diffMs = exp - now;
  const hoursLeft = Math.max(0, Math.floor(diffMs / 3600000));
  const minsLeft = Math.max(0, Math.floor((diffMs % 3600000) / 60000));
  const daysLeft = Math.max(0, Math.floor(diffMs / 86400000));

  const label = isSingle
    ? diffMs > 3600000
      ? `${hoursLeft}h ${minsLeft}m LEFT`
      : `${minsLeft}m LEFT`
    : `EXPIRES ${exp.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;

  const planLabel = isSingle ? "SINGLE" : "MONTHLY";
  const critical = isSingle && hoursLeft < 3;
  const dot = critical ? "bg-yellow-400" : "bg-green-400";

  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-zinc-900/60 border border-zinc-800"
      data-testid="plan-badge"
      title={`Expires ${exp.toLocaleString()}`}
    >
      <div className={`w-1.5 h-1.5 rounded-full ${dot} animate-pulse`} />
      <span className="font-mono text-[9px] tracking-widest text-zinc-300 hidden sm:inline">
        {planLabel}
      </span>
      <span className="font-mono text-[9px] tracking-widest text-zinc-500">·</span>
      <span className="font-mono text-[9px] tracking-widest text-zinc-400" data-testid="plan-expiry">
        {label}
      </span>
    </div>
  );
}
